import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { getAuthStore, useAuthStore } from '@/stores/authStore';
import { logout as apiLogout } from '@/lib/api/client';

export function SiteBrand({ className = 'text-xl' }: { className?: string }): React.ReactElement {
  return (
    <Link to="/" className={`${className} font-bold`} style={{ color: 'var(--text-primary)' }}>
      🐭 Instamouse
    </Link>
  );
}

export function SiteNav({ compact = false }: { compact?: boolean }): React.ReactElement {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const pad = compact ? ' py-1' : '';

  async function handleLogout(): Promise<void> {
    try {
      await apiLogout();
    } finally {
      getAuthStore().getState().logout();
      navigate('/');
    }
  }

  return (
    <nav className="flex items-center gap-4 text-sm">
      {isAuthenticated && user ? (
        <>
          <Link to="/machines/mine">My machines</Link>
          <Link to="/account/billing">Billing</Link>
          <Link to={`/${user.username}`}>{user.display_name}</Link>
          <button className={`btn-secondary${pad}`} onClick={handleLogout}>
            Log out
          </button>
        </>
      ) : (
        <>
          <Link to="/login">Log in</Link>
          <Link to="/register" className={`btn-primary${pad}`}>
            Sign up
          </Link>
        </>
      )}
    </nav>
  );
}

export function Layout({ children }: { children: React.ReactNode }): React.ReactElement {
  // The xpra page wants every pixel: it is pinned to the viewport and draws
  // its own combined site/session bar above the desktop.
  const { pathname } = useLocation();
  if (pathname === '/xpra' || pathname.startsWith('/xpra/')) {
    return <main className="h-[100dvh]">{children}</main>;
  }

  return (
    <div className="min-h-screen grid grid-rows-[auto_1fr_auto]">
      <header
        className="flex items-center justify-between px-6 py-4 border-b"
        style={{ borderColor: 'var(--border)' }}
      >
        <SiteBrand />
        <SiteNav />
      </header>
      <main className="max-w-5xl w-full mx-auto px-6 py-8">{children}</main>
      <footer
        className="px-6 py-6 text-center text-sm border-t"
        style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
      >
        Instamouse — forkable Linux desktops in your browser.
      </footer>
    </div>
  );
}
